import React, { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";

import { AppState } from "shared/data/reducers";
import { useToggle } from "shared/lib/hooks";
import { formattedMessageParams } from "shared/messages";

import Img from "@components/FileServer/Img";
import Modal from "@components/UI/Modal";

import useTimedBoolean from "../../hooks/useTimedBoolean";
import Checkbox from "./Checkbox";

const {
  shareButton,
  modalTitleFlight,
  modalTitlePage,
  copyLabel,
  copiedLabel,
  appendAdditionalInfo,
} = defineMessages({
  shareButton: formattedMessageParams("ShareModal.shareButton"),
  modalTitlePage: formattedMessageParams("ShareModal.title"),
  modalTitleFlight: formattedMessageParams("ShareModal.title.flight"),
  copyLabel: formattedMessageParams("ShareModal.copy"),
  copiedLabel: formattedMessageParams("ShareModal.copied"),
  appendAdditionalInfo: formattedMessageParams(
    "ShareModal.appendAditionalInfo"
  ),
});

const SOCIAL_ICON_SIZE = 32;

type ShareModalProps = {
  url: string;
  title: string;
  additionalInfo: string;
  showSeparator?: boolean;
  className: string;
  showIcon?: boolean;
  setIsSomeModalOpenned?: React.Dispatch<React.SetStateAction<boolean>>;
};

type ShareModalClipboardProps = {
  url: string;
  additionalInfo: string;
};

const ShareModalClipboard = ({
  url,
  additionalInfo,
}: ShareModalClipboardProps) => {
  const intl = useIntl();
  const [sharedInfo, setSharedInfo] = useState(url);
  const [addInformationToggled, toggleAddInformation] = useToggle(true);
  const [copied, triggerCopied] = useTimedBoolean(false, 2500);

  useEffect(() => {
    setSharedInfo(addInformationToggled ? `${additionalInfo}, ${url}` : url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addInformationToggled, url]);

  const isClipboardSupported = navigator?.clipboard;

  const copyToClipboard = async () => {
    if (!copied) {
      triggerCopied();
      navigator?.clipboard.writeText(sharedInfo);
    }
  };

  return (
    <>
      <div className="share-modal-clipboard">
        <input
          defaultValue={sharedInfo}
          className="share-modal-clipboard-input"
          style={{
            padding: "12px 16px",
          }}
        />
        {isClipboardSupported && (
          <div
            role="button"
            className="share-modal-clipboard-button"
            onClick={copyToClipboard}
          >
            {!copied
              ? intl.formatMessage(copyLabel)
              : intl.formatMessage(copiedLabel)}
          </div>
        )}
      </div>
      <div className="share-modal-aditionalinfo-wrapper">
        <Checkbox
          checked={addInformationToggled}
          onClick={toggleAddInformation}
          label={intl.formatMessage(appendAdditionalInfo)}
        />
      </div>
    </>
  );
};

const ShareModal = ({
  url,
  title,
  additionalInfo,
  showSeparator,
  showIcon,
  className,
  setIsSomeModalOpenned,
}: ShareModalProps) => {
  const [isNativeShareAPISupported, setNativeShareAPISupported] = useState(
    false
  );

  const intl = useIntl();
  const cbtToken = useSelector((appState: AppState) => appState.user?.cbtToken);

  const isResults = url.includes("/results");

  const [isToggled, toggle] = useState(false);

  useEffect(() => {
    if (navigator?.canShare) {
      setNativeShareAPISupported(true);
    }
  }, []);

  useEffect(() => {
    if (setIsSomeModalOpenned) {
      setIsSomeModalOpenned(isToggled);
    }
  }, [isToggled, setIsSomeModalOpenned]);

  const toggleOn = () => toggle(true);
  const toggleOff = () => toggle(false);

  const shareButtonClicked = () => {
    if (!isNativeShareAPISupported) {
      toggleOn();
      return;
    }

    navigator.share({
      title,
      url,
      text: additionalInfo,
    });
  };

  if (cbtToken) {
    return null;
  }

  return (
    <>
      {showSeparator && <span className="text-separator"> | </span>}
      <div role="button" className={className} onClick={shareButtonClicked}>
        {showIcon && (
          <Img
            src="/static/images/ico-share.svg"
            style={{ height: 12, marginRight: 3 }}
            alt="share icon"
          />
        )}
        {intl.formatMessage(shareButton)}
      </div>

      <Modal
        isActive={!isNativeShareAPISupported && isToggled}
        onClose={toggleOff}
      >
        <div className="share-modal">
          <div className="share-modal-title">
            {intl.formatMessage(isResults ? modalTitlePage : modalTitleFlight)}
          </div>
          <div className="share-modal-socials">
            <EmailShareButton
              title={title}
              subject={title}
              body={additionalInfo}
              url={url}
              separator=", "
            >
              <div className="share-modal-social-description">
                <EmailIcon size={SOCIAL_ICON_SIZE} round />
                <div className="share-modal-social-description-label">
                  Email
                </div>
              </div>
            </EmailShareButton>
            <FacebookShareButton url={url} quote={title}>
              <div className="share-modal-social-description">
                <FacebookIcon size={SOCIAL_ICON_SIZE} round />
                <div className="share-modal-social-description-label">
                  Facebook
                </div>
              </div>
            </FacebookShareButton>
            <TwitterShareButton url={url} title={title}>
              <div className="share-modal-social-description">
                <TwitterIcon size={SOCIAL_ICON_SIZE} round />
                <div className="share-modal-social-description-label">
                  Twitter
                </div>
              </div>
            </TwitterShareButton>
            <WhatsappShareButton url={url} title={title} separator=", ">
              <div className="share-modal-social-description">
                <WhatsappIcon size={SOCIAL_ICON_SIZE} round />
                <div className="share-modal-social-description-label">
                  WhatsApp
                </div>
              </div>
            </WhatsappShareButton>
          </div>
          {isToggled && (
            <ShareModalClipboard url={url} additionalInfo={additionalInfo} />
          )}
        </div>
      </Modal>
    </>
  );
};

export default ShareModal;
